/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useAuth } from '../context/AuthContext';
import { tenantAdminAllows } from '../utils/product-permission-context.utils';
import { getCompanyScopeSnapshot } from '../utils/company-scope-lifecycle.utils';
import { getApplicationCodeForApi } from '../config/app.config';

/**
 * İzin kontrolleri (rol tabanlı erişim) için Hook.
 */
export function usePermission() {
  const { user } = useAuth();

  /**
   * Kullanıcının belirli bir permission'a sahip olup olmadığını kontrol et
   */
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    // Admin tüm yetkilere sahip
    if (user.isAdmin || user.permissions?.includes('*')) {
      return true;
    }
    const scope = getCompanyScopeSnapshot();
    if (!scope.blocked && tenantAdminAllows(user, permission,
      { appCode: getApplicationCodeForApi(), companyId: scope.companyId })) return true;

    // Belirli yetki kontrolü
    return user.permissions?.includes(permission) || false;
  };

  /**
   * Kullanıcının belirli bir modül için oluşturma (create) yetkisine sahip olup olmadığını kontrol et
   */
  const canCreate = (module: string): boolean => {
    return hasPermission(`${module}:create`);
  };

  /**
   * Kullanıcının belirli bir modül için okuma (read) yetkisine sahip olup olmadığını kontrol et
   */
  const canRead = (module: string): boolean => {
    return hasPermission(`${module}:read`);
  };

  /**
   * Kullanıcının belirli bir modül için güncelleme (update) yetkisine sahip olup olmadığını kontrol et
   */
  const canUpdate = (module: string): boolean => {
    return hasPermission(`${module}:update`);
  };

  /**
   * Kullanıcının belirli bir modül için silme (delete) yetkisine sahip olup olmadığını kontrol et
   */
  const canDelete = (module: string): boolean => {
    return hasPermission(`${module}:delete`);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user || permissions.length === 0) return false;
    if (user.isAdmin || user.permissions?.includes('*')) {
      return true;
    }
    return permissions.some(hasPermission);
  };

  return {
    hasPermission,
    hasAnyPermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
  };
}

